import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from './offer'
import { INTL_DAY_MONTH_NAME_SHORT } from './dateFormats'
import { OfferContextMap } from 'analytics/snowplow/context'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import Places from 'constants/places'
import { LUXURY_ESCAPES } from 'constants/brands'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

export const TOUR_CHILD_MAX_AGE = 15
export const TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD = 105

export const DIRECT_TOUR = 'direct_tour'
export const PARTNER_TOUR = 'partner_tour'
export const CONNECTION_TOUR = 'connection_tour'
export const CRUISE_TOUR = 'cruise_tour'

export const ULTRA_LUX_TOUR = 'ultra_lux_tour'

export const TOUR_ARTEFACT_SIZE_MAX_BYTES = 5 * 1024 * 1024
export const TOUR_ARTEFACT_UPLOAD_DEADLINE_DAYS_BEFORE_DEPARTURE = 7
export const TOUR_ARTEFACT_RETENTION_DAYS_AFTER_DEPARTURE = 7

/** Customer self-service edits to submitted tour traveller details are allowed until this many hours before departure (instant on stored departure timestamp). */
export const TOUR_TRAVELLER_SELF_EDIT_MIN_HOURS_BEFORE_DEPARTURE = 72

export const TOUR_V2_OFFER_LIST_TYPES: Array<App.Tours.V2ParentOfferType> = [OFFER_TYPE_TOUR_V2]
export const TOUR_V1_OFFER_LIST_TYPES: Array<App.OfferType> = [OFFER_TYPE_TOUR]
export const TOUR_V1_OFFER_LIST_FILTER_TYPES: Array<App.OfferType> = [OFFER_TYPE_TOUR]
export const TOURS_OFFER_LIST_EXCLUDED_PARTNERSHIP_CODES = new Set([
  'qantas',
  'velocity',
])

export const TOUR_ORDER_TRAVELLER_DETAILS_TTL: number = 300 // in seconds

export enum TourV2ContentSections {
  GALLERY = 'gallery',
  OVERVIEW = 'overview',
  ITINERARY = 'itinerary',
  VARIATIONS = 'variations',
  PREFERRED_PACKAGES = 'preferred-packages',
  INCLUSIONS = 'inclusions',
  DEPARTURES = 'dates-and-pricing',
  DEPARTURE_DATES = 'departure_dates',
  FINE_PRINT = 'things-to-know',
  FAQ = 'frequently-asked-questions',
  YOU_MAY_ALSO_LIKE = 'you-may-also-like',
  REVIEWS = 'reviews',
  ADDITIONAL_WAYS = 'additional-ways',
  SIMILAR_TOURS = 'similar-tours',
}

export const TOUR_V2_STICKY_HEADER_ENABLED_SECTIONS = new Set<TourV2ContentSections>([
  TourV2ContentSections.OVERVIEW,
  TourV2ContentSections.ITINERARY,
  TourV2ContentSections.VARIATIONS,
  TourV2ContentSections.PREFERRED_PACKAGES,
  TourV2ContentSections.INCLUSIONS,
  TourV2ContentSections.DEPARTURES,
  TourV2ContentSections.FINE_PRINT,
  TourV2ContentSections.FAQ,
  TourV2ContentSections.REVIEWS,
])

export const TOUR_V2_TARGET_OCCUPANT_TYPES: Set<App.OccupantType> = new Set(['adults', 'children'])

export const TOUR_V2_SEARCH_TYPES: Array<App.SearchType> = ['destination', 'tour']

export const TOUR_V2_SEARCH_HERO_IMAGE_ID = 'adkvxdgnii1kvb4s970c'
export const TOUR_V2_CATALOGUE_HERO_IMAGE_ID = 'tour-catalog-header'
export const TOUR_V2_SEARCH_HERO_IMAGE_ASIA_CAMPAIGN = 'tour-hero-asia-campaign-background-v2'
export const TOUR_V2_HERO_IMAGE_ASIA_MOBILE_BACKGROUND = 'tour-hero-asia-campaign-mobile-background-v2'
export const TOUR_V2_PROFILE_ASIA_CAMPAIGN = 'profile-asia-campaign'
export const TOUR_ASIA_CAMPAIGN = 'TOURASIA'
export const TOUR_PROMO_CODE = 'TOURSALE'
export const TOUR_PROMO_END_DATE = '2025-01-31T00:00:00Z'

const TOUR_2027_LAUNCH_TERMS_AND_CONDITIONS = `*Receive up to A$1000 per person in flight credit when purchasing a qualifying 2027 Luxury Escapes Tour (Qualifying Purchase). Credit is delivered to your account within 7 days of Qualifying Purchase. Book eligible flights by 28 February 2027. Travel may occur after this date, subject to airline schedules and fare availability. Valid on Australian international return flights, booked via Luxury Escapes. Not valid for domestic flights. Applies to full service carriers only - airline and geographic exclusions apply. Credit applies to the total airfare charge at checkout; any remaining balance is payable by you. A single-use voucher is issued per Qualifying Purchase - only one voucher may be redeemed per flight order. See full terms here: [https://luxuryescapes.com/au/promotions](https://luxuryescapes.com/au/promotions).*

**Full Terms and Conditions**

Qualifying 2027 Luxury Escapes Tours include a flight credit per person. The credit amount: $200, $400, $500 or $1,000, is determined by Luxury Escapes based on the specific tour and will be displayed on the tour page. Promotion applies to bookings from 31 March 2026 to 14 May 2026 when purchasing a Tour marked *"Flight Credit for 2027 Tours"* with a scheduled departure on or after 1 January 2027.

Credit will be added to your Luxury Escapes Wallet within 7 days of your Qualifying Purchase becoming non cancellable under our booking terms. Credits are denominated per passenger and accrue in the primary account holder's Luxury Escapes Account Wallet. Each Qualifying Purchase generates a single use voucher. Only one voucher may be redeemed per flight order.

**Scope and exclusions**

Credit can only be redeemed on flights booked through Luxury Escapes. Not valid for airline direct or third party purchases. Credit is valid only for international return itineraries that depart from and return to Australia. Not valid for domestic flights or travel to/from New Zealand. $400 credit: Not valid for travel to/from Asia or South Pacific. $500 credit and $500 + BONUS Business Class: Not valid for travel to/from Asia, South Pacific, Middle East or Africa routes.

Your credit is applied to the total airfare including taxes/fees. If the total airfare is less than the credit amount, the remaining credits will be forfeited. Credit is valid on full service carriers only and excludes the following carriers or brands:

<table>
  <tbody>
    <tr>
      <td>
        <ul>
          <li>JQ Jetstar</li>
          <li>TR Scoot</li>
          <li>D7 AirAsia X</li>
        </ul>
      </td>
      <td>
        <ul>
          <li>AK AirAsia</li>
          <li>5J Cebu Pacific</li>
          <li>VJ VietJet Air</li>
        </ul>
      </td>
      <td>
        <ul>
          <li>OD Batik Air Malaysia</li>
          <li>JT Lion Air</li>
          <li>ZL Rex</li>
        </ul>
      </td>
    </tr>
  </tbody>
</table>

**Business Class BONUS**

Available only on the select Europe tours.

A credit of up to $1,000 per eligible passenger is applied automatically at checkout when a Business Class fare is selected for the above listed tours. If the flight is changed to Economy or cancelled/refunded, the BONUS portion is forfeited or reversed. If rebooked to Business Class within the Redemption Window, we may re apply the bonus at our discretion, subject to availability and these terms.

**Expiry and cancellations**

Flights must be booked by 11:59pm AEDT on 28 February 2027; travel may occur later in 2027, subject to airline schedule availability.

If your Tour or Flight is cancelled or refunded for any reason, any associated credit not yet used will be cancelled, the credit component is not refundable in cash and, unless required by law, is forfeited on refund.

**General**

The credit is non transferable, has no cash value and is not legal tender. This offer is not combinable with other flight credits or promo codes unless otherwise expressly stated. We may cancel credit and/or bookings obtained in breach of these terms. Full terms and conditions apply - see [https://luxuryescapes.com/au/terms-and-conditions](https://luxuryescapes.com/au/terms-and-conditions).`

export const TOUR_2027_LAUNCH_CAMPAIGN_CONFIG: App.Tours.TourCampaign = {
  name: '2027 Tours SALE',
  tag: 'TOUR_2027_LAUNCH',
  brands: [LUXURY_ESCAPES],
  campaignLabel: 'Special Offer',
  message: 'Earn up to $500 per person Flight Credit on new 2027 Tours (plus up to $1000 on Europe Business Class)',
  endDate: '2026-05-14T23:59:59+10:00',
  regionCode: 'AU',
  searchFilterTag: 'Tours 2027',
  tiers: [
    {
      id: 'tier1',
      name: 'Tier 1',
      tag: '$200pp Flight Credit for 2027 Tours',
      cta: 'Book now and earn $200 per person flight credit on 2027 departures',
      termsAndConditions: TOUR_2027_LAUNCH_TERMS_AND_CONDITIONS,
      tourIds: [
        'tour-1c41669f-b341-4fca-9f97-79b44b7b1df9', // Staging
        'tour-c042024e-f7be-4128-a78a-6f7a7916dd1c',
        'tour-12beff81-359b-45a8-8e2a-94b854842461',
        'tour-f85079f2-463d-491e-b082-3b8c47fbd6f1',
        'tour-26425287-63ef-434f-8069-3cfc569915c4',
        'tour-9af4e71d-3f0c-46fd-a559-c25fd3c27a16',
        'tour-12f21763-5883-4e6c-8f8e-d3f7ea23e667',
        'tour-739af170-e426-47e2-8e22-e7ba75560ca6',
        'tour-cfe20ed8-253e-42fa-921a-f9c573cdf5ce',
        'tour-04ee41d8-addd-4799-8891-65523b25b518',
        'tour-71316ee0-2589-4690-9b3b-11705fea7272',
        'tour-b3634a72-5fe1-4bc1-8906-415e8e7c0e09',
        'tour-8a02cc90-bb10-46e7-ab53-9b9873013a1e',
        'tour-18a1e658-bd65-4f93-9318-60de5c33733e',
        'tour-0b1e0249-7c92-491f-9e07-1c98a6e38128',
        'tour-c70a5d35-e65d-4b11-9871-65f4e9e2777d',
        'tour-afd3cb64-1131-457a-bce0-078cd0711fbf',
        'tour-de6e7a67-656b-40b0-b15b-86241e7755d4',
        'tour-92d2174b-1048-437e-92a7-82e4cc4fe56d',
      ],
    },
    {
      id: 'tier2',
      name: 'Tier 2',
      tag: '$400pp Flight Credit for 2027 Tours',
      cta: 'Book now and earn $400 per person flight credit on 2027 departures',
      termsAndConditions: TOUR_2027_LAUNCH_TERMS_AND_CONDITIONS,
      tourIds: [
        'tour-6bcd340e-eced-430d-b1aa-aca48e939fed', // Staging
        'tour-4df6d448-293e-458c-aad7-abb91808126b',
        'tour-cead4c1e-86d7-4804-9c65-6f1d07fece89',
        'tour-30de98ab-53fc-4c8e-87da-3cf0acc93aaf',
        'tour-49acfa5d-e6ab-4717-aa5c-acb1f353620e',
        'tour-35cbb219-09e6-40d0-af6d-8e247e2a31eb',
        'tour-b2107179-0c88-4706-be0c-9b77abbd7022',
        'tour-832e931c-0eec-4a03-8d96-95589ed129b2',
        'tour-8bc0b186-7f3c-4216-863d-8b12cc1c2ed0',
        'tour-c68a5eea-fe29-4fe2-af3b-8c4ae202d8cc',
        'tour-e333c634-e580-4863-879d-743510aca84e',
        'tour-62107d17-23be-43f6-8962-45764aa1235a',
        'tour-358efb13-3c19-4238-a972-3a9f61187e3e',
        'tour-e71f33ac-07e3-4f10-9a3c-e2a451767418',
      ],
    },
    {
      id: 'tier3',
      name: 'Tier 3',
      tag: '$500pp Flight Credit for 2027 Tours',
      cta: 'Book now and earn $500 per person flight credit on 2027 departures',
      termsAndConditions: TOUR_2027_LAUNCH_TERMS_AND_CONDITIONS,
      tourIds: [
        'tour-106e7a43-9b7d-4089-bf66-9af5b8ac4607', // Staging
        'tour-72810fd2-41c1-4eac-8767-e5d51f6ab402',
        'tour-dbebbdc2-95d6-4a2f-9cb1-dde9bb9c0bd3',
      ],
    },
    {
      id: 'tier4',
      name: 'Tier 4',
      tag: 'Up to $1,000pp Flight Credit for 2027 Tours*',
      cta: 'Book now and earn $500 per person flight credit on 2027 departures (plus up to $1,000 per person on Business Class)',
      termsAndConditions: TOUR_2027_LAUNCH_TERMS_AND_CONDITIONS,
      tourIds: [
        'tour-038d6b2d-efd6-4554-b1fe-10907a72b301', // Staging
        'tour-bdd2ac92-be6c-4758-913f-29202d56cfdb',
        'tour-e2588994-37c8-4035-b731-452302fd014a',
        'tour-19842834-2186-4046-9d0c-52a4421a8431',
        'tour-6e43415c-e822-48c3-82df-7ad5e241d2cf',
        'tour-98ff7827-4a03-4a42-9e72-293e4cf5a047',
        'tour-274070fe-000c-4e80-a1d3-c262c6649538',
      ],
    },
  ],
}

export type TourSearchSpecialOfferCampaignIntlKey = 'tours2027'

export interface TourSearchSpecialOfferCampaignConfig {
  brands?: Array<string>
  displayLabel: string
  endDate?: string
  intlKey: TourSearchSpecialOfferCampaignIntlKey
  regionCodes?: Array<string>
  tag: string
}

export const TOUR_SEARCH_SPECIAL_OFFERS_CAMPAIGNS: Array<TourSearchSpecialOfferCampaignConfig> = [
  {
    tag: TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.searchFilterTag ?? '',
    displayLabel: '2027 Tours',
    intlKey: 'tours2027',
    endDate: TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.endDate,
    brands: TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.brands,
    regionCodes: TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.regionCode ? [TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.regionCode] : undefined,
  },
]

export function getTourPromoCodeUrgencyMessage(): string {
  const intl = getCurrentIntl()

  return intl.formatMessage(
    {
      id: 'MnmNqXOsuRsKEO41ggSz',
      defaultMessage: 'Code: {promoCode} ends {endDate}',
      description: 'Urgency message shown on tours offers with the promo code and its ending date',
    },
    {
      promoCode: TOUR_PROMO_CODE,
      endDate: intlDateFormatter(
        TOUR_PROMO_END_DATE,
        INTL_DAY_MONTH_NAME_SHORT,
        intl.locale,
        'UTC',
      ),
    },
  )
}

export function getTour2027LaunchCampaignTier(tourId: string): App.Tours.TourCampaignTier | undefined {
  return TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.tiers.find(({ tourIds }) => tourIds.includes(tourId))
}

export function isTourSearchSpecialOfferCampaignActive(
  campaign: TourSearchSpecialOfferCampaignConfig,
  regionCode: string,
  brand: string,
): boolean {
  if (campaign.brands?.length && !campaign.brands.includes(brand)) {
    return false
  }

  if (campaign.regionCodes?.length && !campaign.regionCodes.includes(regionCode)) {
    return false
  }

  if (!campaign.endDate) {
    return true
  }

  const campaignEndTime = new Date(campaign.endDate).getTime()

  return !Number.isNaN(campaignEndTime) && Date.now() <= campaignEndTime
}

export function isTour2027LaunchCampaignActive(regionCode?: string, brand?: string): boolean {
  const campaignEndTime = new Date(TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.endDate).getTime()
  const isBrandAllowed = !TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.brands?.length
    || Boolean(brand && TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.brands.includes(brand))

  return isBrandAllowed
    && regionCode === TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.regionCode
    && !Number.isNaN(campaignEndTime)
    && Date.now() <= campaignEndTime
}

export const TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES: Array<App.SearchPlaceType> = [
  'city',
  'colloquial_area',
  'continent',
  'country',
  'high_level_region',
  'multi_city_vicinity',
  'province_state',
]

export const TOUR_MINIMUM_VALUED_AT_PERCENTAGE_TO_SHOW = 5

export const DEFAULT_TOUR_V2_OFFER_MAX_CHILD_AGE = 17

export const DEFAULT_TOUR_V2_PRIVATE_MAX_CHILD_AGE = 15

export const VendorsThatRequireTravellerDetails = new Set(['00128000018BZkIAAW', '0010I00001ba7JdQAI', '0010I00001f6bHvQAI', '0010I000028DrsxQAC', '0012y00000LHHZoAAP'])

export const TOUR_V2_MALE_TRAVELLER_TITLES = ['Mr', 'Master']

export const TOUR_V2_FEMALE_TRAVELLER_TITLES = ['Ms', 'Mrs', 'Miss']

export const TOUR_V2_LE_SOURCE = 'salesforce'

export const TOUR_V2_PACKAGE_OPTION_BASE = 'Base'
export const TOUR_V2_PACKAGE_OPTION_EXTENDED = 'Extended'
export const TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE = 'Extended Alternative'

export const tourOptionPackages = [
  TOUR_V2_PACKAGE_OPTION_BASE,
  TOUR_V2_PACKAGE_OPTION_EXTENDED,
  TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE,
]

export const tourOptionPackagesEvent = {
  [TOUR_V2_PACKAGE_OPTION_BASE]: OfferContextMap.VIEW_TOUR_OPTIONS_PRESSED,
  [TOUR_V2_PACKAGE_OPTION_EXTENDED]: OfferContextMap.VIEW_TOUR_OPTIONS_EXTENDED_PRESSED,
  [TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE]: OfferContextMap.VIEW_TOUR_OPTIONS_EXTENDED_ALT_PRESSED,
}

export enum PACKAGE_UPGRADE_VALUES {
  REGULAR = 'regular',
  VIP = 'vip',
  PLATINUM = 'platinum',
}

export const TourOptionPackageUpgrades = [PACKAGE_UPGRADE_VALUES.REGULAR, PACKAGE_UPGRADE_VALUES.VIP, PACKAGE_UPGRADE_VALUES.PLATINUM]

export const SUPERIOR_PACKAGE_OPTIONS_AVAILABLES: Record<string, Array<PACKAGE_UPGRADE_VALUES>> = {
  [PACKAGE_UPGRADE_VALUES.REGULAR]: TourOptionPackageUpgrades,
  [PACKAGE_UPGRADE_VALUES.VIP]: [PACKAGE_UPGRADE_VALUES.VIP, PACKAGE_UPGRADE_VALUES.PLATINUM],
  [PACKAGE_UPGRADE_VALUES.PLATINUM]: [PACKAGE_UPGRADE_VALUES.PLATINUM],
}

export const ALL_TOUR_PACKAGE_TYPE = 'all'

export const getOfferPackageUpgradeDisplayTextMap = (offerId: string) => {
  const intl = getCurrentIntl()
  const defaultPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage({
      id: 'b9palHdolSvMp-IlZZ2i',
      defaultMessage: 'Standard Package',
      description: 'Default package upgrade label for the regular tour package option',
    })],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage({
      id: 'h2_EtFC2HWhSVQilUwNQ',
      defaultMessage: 'Upgraded Package',
      description: 'Default package upgrade label for the VIP tour package option',
    })],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage({
      id: 'jzV5qmTIVdVTCeKuqzLS',
      defaultMessage: 'Deluxe Package',
      description: 'Default package upgrade label for the platinum tour package option',
    })],
  ])
  const ecoBusinessPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage({
      id: '4I2qlEMsRpcT93YPf1s2',
      defaultMessage: 'Economy Package',
      description: 'Special package upgrade label for the regular tour package option on economy/business style tours',
    })],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage({
      id: 'qXZu8tX8sT1bYL8T4v2l',
      defaultMessage: 'Business Package',
      description: 'Special package upgrade label for the VIP tour package option on economy/business style tours',
    })],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage({
      id: 'Ih1cWqlfC1yBu12IQMrl',
      defaultMessage: 'First Class Package',
      description: 'Special package upgrade label for the platinum tour package option on economy/business style tours',
    })],
  ])
  const viewPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage({
      id: 'YiMyIVtwzdPrffMBefv_',
      defaultMessage: 'King or Twin Package',
      description: 'Package upgrade label for the regular tour package option on room-view tour variants',
    })],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage({
      id: 'eh62fJvhKPjvq2vGL5fq',
      defaultMessage: 'Ocean View w. Club Access Upgrade',
      description: 'Package upgrade label for the VIP tour package option on room-view tour variants',
    })],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage({
      id: 'KuSXMjpOM85v7PM4_QRw',
      defaultMessage: 'Regency Suite w. Club Access Upgrade',
      description: 'Package upgrade label for the platinum tour package option on room-view tour variants',
    })],
  ])
  const rangeViewPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage({
      id: 'nZiKFk8reb-EFci5sdrq',
      defaultMessage: 'Classic to King or Twin Package',
      description: 'Package upgrade label for the regular tour package option on room-range tour variants',
    })],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage({
      id: 'xPT7_Lg-DWkegz1fhgO-',
      defaultMessage: 'Superior to Ocean View w. Club Access Upgrade',
      description: 'Package upgrade label for the VIP tour package option on room-range tour variants',
    })],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage({
      id: '7FPVBz9Ex7TA4dhDwHSx',
      defaultMessage: 'Deluxe to Regency Suite w. Club Access Upgrade',
      description: 'Package upgrade label for the platinum tour package option on room-range tour variants',
    })],
  ])

  switch (offerId?.replace('tour-', '')) {
    case 'bbf97caa-d857-4517-b768-465f5ceac8db':
    case '15275af7-48cb-4a5e-ad0c-eb9b3efc1619':
    case '6f77e732-d5e1-4747-ae47-901022e36fc3':
      return ecoBusinessPackageUpgradeDisplayTextMap
    case '6909c91b-cc92-4c0b-86d4-d663263436be':
      return viewPackageUpgradeDisplayTextMap
    case '1ea35824-8354-4656-9d76-c24d717ee700':
      return rangeViewPackageUpgradeDisplayTextMap
    default:
      return defaultPackageUpgradeDisplayTextMap
  }
}

export enum TOURS_GROUP_TYPE {
  isPrivateRequest = 'isPrivateRequest',
  isSmallGroup = 'isSmallGroup',
  isLargeGroup = 'isLargeGroup',
  isFamilyFriendly = 'isFamilyFriendly',
}

export const TOURS_GROUP_TYPES_LIST = Object.values(TOURS_GROUP_TYPE)

export enum TOUR_OFFER_TAG {
  onSale = 'onSale',
  flashOffer = 'flashOffer',
}

export const TOUR_OFFER_TAG_LIST = Object.values(TOUR_OFFER_TAG)

export const TOUR_MAX_ROOM_LIMIT = 5

export const TOUR_OPTIONAL_EXPERIENCE_BOOKING_DAYS_BEFORE_DEPARTURE = 60

export const TOUR_OPTIONAL_EXPERIENCE_RESERVATION_STATUS_CONFIRMED = 'confirmed'
export const TOUR_OPTIONAL_EXPERIENCE_ITEM_STATUS_COMPLETED = 'completed'

export const BRAND_SIGNATURE_SERIES = 'lesignaturetours'

export const PROMO_CODE_MAX_DISCOUNT_REGION: Record<string, string> = {
  AU: formatCurrency(1000, 'AUD', 'AU', { format: 'dynamic' }),
  NZ: formatCurrency(1000, 'NZD', 'NZ', { format: 'dynamic' }),
  SG: formatCurrency(1000, 'SGD', 'SG', { format: 'dynamic' }),
  US: formatCurrency(600, 'USD', 'US', { format: 'dynamic' }),
  GB: formatCurrency(500, 'GBP', 'GB', { format: 'dynamic' }),
  HK: formatCurrency(5000, 'HKD', 'HK', { format: 'dynamic' }),
}

export const PROMO_SPEND_EARN_REGION: Record<string, Array<{ spend: number, earn: number }>> = {
  AU: [{ spend: 4999, earn: 250 }, { spend: 6999, earn: 300 }, { spend: 9999, earn: 500 }, { spend: 19999, earn: 750 }],
  NZ: [{ spend: 5499, earn: 250 }, { spend: 7499, earn: 300 }, { spend: 10499, earn: 500 }, { spend: 15999, earn: 750 }],
  SG: [{ spend: 4499, earn: 250 }, { spend: 6499, earn: 300 }, { spend: 9499, earn: 500 }, { spend: 13999, earn: 750 }],
  HK: [{ spend: 24999, earn: 1250 }, { spend: 33999, earn: 1500 }, { spend: 49999, earn: 2500 }, { spend: 74999, earn: 3750 }],
  GB: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 149 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  US: [{ spend: 2299, earn: 150 }, { spend: 4499, earn: 200 }, { spend: 6499, earn: 300 }, { spend: 12999, earn: 500 }],
  CA: [{ spend: 2299, earn: 150 }, { spend: 4499, earn: 200 }, { spend: 6499, earn: 300 }, { spend: 12999, earn: 500 }],
  FR: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  IT: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  ES: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  NL: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  DE: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  IE: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
}

export const EXCLUDE_OFFER_PROMO_SPEND_EARN = ['tour-b9fcebec-2e68-42d5-a5b5-b09c69930d87']
export const END_SPEND_EARN_PROMO_TAG_DATE = new Date('2025-03-10')

export const TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS = [
  Places.India.id,
  Places.SriLanka.id,
  Places.Thailand.id,
  Places.Vietnam.id,
  Places.Japan.id,
  Places.China.id,
  Places.Malaysia.id,
  Places.Nepal.id,
]
export const TOUR_ASIA_TAKEOVER_SHOW_SEARCH_BANNER_PLACE_IDS = [
  ...TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
  Places.Anywhere.id,
  Places.Asia.id,
  Places.SouthEastAsia.id,
]

export const TOUR_HERO_IMAGES_IDS_ROTATION: Array<{ id: string, gravity: 'north' | 'center' }> = [
  { id: 'bxwkiui0zplo7rexp1sp', gravity: 'center' },
  { id: 'buxxor3j5kmy5vqo9klc', gravity: 'center' },
  { id: 'byfoq3zduoearfn090g', gravity: 'north' },
  { id: 'l0clydv7291tl39ud6', gravity: 'center' },
  { id: 'wp4rzuxk8fzq899bpd', gravity: 'center' },
]

export const TOUR_DEFAULT_DEAL_HERO_IMAGE_ID = 'b1c309a3-eb76-4e00-8748-bdf59fd7d714'
export const TOUR_DEFAULT_DEAL_HIGHLIGHT_COLOUR = '#232B5C'

export const TOUR_ASIA_PROMO_REGIONS: Record<string, number> = {
  AU: 849,
  NZ: 920,
  US: 520,
  GB: 430,
  SG: 710,
  HK: 4090,
  CA: 760,
  FR: 490,
  IT: 490,
  ES: 490,
  NL: 490,
  DE: 490,
  IE: 490,
}
